import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CalendarDays,
  Check,
  CircleCheck,
  ExternalLink,
  PartyPopper,
  PenSquare,
  Plug,
  Plus,
  Sparkles,
  Trash2,
  Wand2,
} from 'lucide-react';
import type { PlatformConnectionStatus } from '../../shared/api';
import type { BusinessProfileInput, PlatformId, WritingSample } from '../../shared/types';
import { PLATFORM_LIST, PLATFORM_SPECS } from '../../shared/platforms';
import { normalizeHashtags } from '../../shared/utils';
import { ThemeToggle } from '../components/layout';
import {
  Alert,
  Badge,
  Button,
  detectTimezone,
  Field,
  Input,
  PlatformIcon,
  Skeleton,
  Steps,
  Textarea,
  TimezoneSelect,
  useToast,
} from '../components/ui';
import type { StepDescriptor } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { qk } from '../lib/query';
import { useSessionStore } from '../stores/session';
import { useWorkspaceStore } from '../stores/workspace';
import { cn } from '../lib/utils';

const STEPS: StepDescriptor[] = [
  { id: 'workspace', label: 'Workspace' },
  { id: 'business', label: 'Business', optional: true },
  { id: 'connect', label: 'Connect', optional: true },
  { id: 'voice', label: 'Your voice', optional: true },
  { id: 'done', label: 'Ready' },
];

/* ================================================================== */
/* Step 1 — workspace                                                  */
/* ================================================================== */

function WorkspaceStep({ onDone }: { onDone: () => void }) {
  const workspaces = useSessionStore((state) => state.workspaces);
  const refresh = useSessionStore((state) => state.refresh);
  const setWorkspace = useWorkspaceStore((state) => state.setWorkspace);
  const activeId = useWorkspaceStore((state) => state.workspaceId);

  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState(detectTimezone);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.workspaces.create({ name: name.trim(), timezone }),
    onSuccess: async (response) => {
      setWorkspace(response.workspace.id);
      await refresh();
      onDone();
    },
    onError: (mutationError) => {
      setError(
        mutationError instanceof ApiError
          ? mutationError.message
          : 'Could not create the workspace.',
      );
    },
  });

  const existing = workspaces.find((workspace) => workspace.id === activeId) ?? workspaces[0];

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        if (!name.trim()) {
          setError('Give the workspace a name.');
          return;
        }
        setError(null);
        create.mutate();
      }}
    >
      <Field
        label="Workspace name"
        htmlFor="ws-name"
        required
        error={error}
        hint="Usually the business you publish for — it appears in the switcher and on invites."
      >
        <Input
          id="ws-name"
          autoFocus
          inputSize="lg"
          placeholder="Northwind Coffee"
          value={name}
          invalid={Boolean(error)}
          onChange={(event) => {
            setName(event.target.value);
            if (error) setError(null);
          }}
        />
      </Field>

      <Field
        label="Timezone"
        htmlFor="ws-timezone"
        hint="Schedules, the calendar and “publish at 9am” all use this zone."
      >
        <TimezoneSelect
          id="ws-timezone"
          value={timezone}
          onChange={setTimezone}
          selectSize="lg"
        />
      </Field>

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button
          type="submit"
          size="lg"
          loading={create.isPending}
          loadingLabel="Creating the workspace"
          rightIcon={<ArrowRight className="size-4" />}
        >
          Create workspace
        </Button>
        {existing && (
          <Button type="button" variant="ghost" onClick={onDone}>
            Continue with {existing.name}
          </Button>
        )}
      </div>
    </form>
  );
}

/* ================================================================== */
/* Step 2 — business profile                                           */
/* ================================================================== */

interface BusinessForm {
  description: string;
  industry: string;
  audience: string;
  toneDefault: string;
  website: string;
  hashtags: string;
}

const TONE_PRESETS = [
  'Warm and conversational',
  'Confident and direct',
  'Playful and witty',
  'Expert and measured',
] as const;

function BusinessStep({ workspaceId, onDone }: { workspaceId: string; onDone: () => void }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const profile = useQuery({
    queryKey: qk.businessProfile(workspaceId),
    queryFn: () => api.workspaces.businessProfile(workspaceId),
    retry: false,
  });

  const [form, setForm] = useState<BusinessForm>({
    description: '',
    industry: '',
    audience: '',
    toneDefault: '',
    website: '',
    hashtags: '',
  });

  // Prefill once the existing profile arrives (setup may already have one).
  useEffect(() => {
    const existing = profile.data?.profile;
    if (!existing) return;
    setForm({
      description: existing.description ?? '',
      industry: existing.industry ?? '',
      audience: existing.audience ?? '',
      toneDefault: existing.toneDefault ?? '',
      website: existing.website ?? '',
      hashtags: (existing.defaultHashtags ?? []).join(' '),
    });
  }, [profile.data]);

  const save = useMutation({
    mutationFn: () => {
      const body: BusinessProfileInput = {
        description: form.description.trim() || null,
        industry: form.industry.trim() || null,
        audience: form.audience.trim() || null,
        toneDefault: form.toneDefault.trim() || null,
        website: form.website.trim() || null,
        defaultHashtags: normalizeHashtags(form.hashtags.split(/[\s,]+/)),
      };
      return api.workspaces.saveBusinessProfile(workspaceId, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.businessProfile(workspaceId) });
      toast({ title: 'Business profile saved', tone: 'success' });
      onDone();
    },
    onError: (error) => {
      toast({
        title: 'Could not save the profile',
        description: error instanceof ApiError ? error.message : undefined,
        tone: 'error',
      });
    },
  });

  const update = <Key extends keyof BusinessForm>(key: Key, value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  if (profile.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-2/3" />
      </div>
    );
  }

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <Alert tone="info" title="This is the context behind every AI draft">
        The more specific you are, the less editing you do later. All of it stays editable on the
        Brand page.
      </Alert>

      <Field
        label="What does the business do?"
        htmlFor="biz-description"
        hint="Two or three sentences. Say what you sell and what makes it different."
      >
        <Textarea
          id="biz-description"
          rows={4}
          maxRows={10}
          placeholder="We roast small-batch coffee in Austin and ship it within 24 hours of roasting. Our customers care about traceability and a consistent morning cup."
          value={form.description}
          onChange={(event) => update('description', event.target.value)}
        />
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Industry" htmlFor="biz-industry">
          <Input
            id="biz-industry"
            placeholder="Specialty coffee retail"
            value={form.industry}
            onChange={(event) => update('industry', event.target.value)}
          />
        </Field>
        <Field label="Website" htmlFor="biz-website">
          <Input
            id="biz-website"
            type="url"
            placeholder="https://northwind.coffee"
            value={form.website}
            onChange={(event) => update('website', event.target.value)}
          />
        </Field>
      </div>

      <Field
        label="Who are you talking to?"
        htmlFor="biz-audience"
        hint="Your audience, in their words rather than a demographic bracket."
      >
        <Input
          id="biz-audience"
          placeholder="Home brewers who grind their own beans"
          value={form.audience}
          onChange={(event) => update('audience', event.target.value)}
        />
      </Field>

      <Field label="Default tone" htmlFor="biz-tone">
        <Input
          id="biz-tone"
          placeholder="Warm and conversational"
          value={form.toneDefault}
          onChange={(event) => update('toneDefault', event.target.value)}
        />
        <div className="flex flex-wrap gap-1.5 pt-2">
          {TONE_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => update('toneDefault', preset)}
              className={cn(
                'rounded-full border px-2.5 py-1 text-xs transition-colors',
                form.toneDefault === preset
                  ? 'border-accent bg-accent-soft text-accent'
                  : 'border-border text-muted hover:border-border-strong hover:text-fg',
              )}
            >
              {preset}
            </button>
          ))}
        </div>
      </Field>

      <Field
        label="Default hashtags"
        htmlFor="biz-hashtags"
        hint="Optional. Separate with spaces — they are suggested, never forced."
      >
        <Input
          id="biz-hashtags"
          placeholder="#specialtycoffee #austin"
          value={form.hashtags}
          onChange={(event) => update('hashtags', event.target.value)}
        />
      </Field>

      <Button
        type="submit"
        size="lg"
        loading={save.isPending}
        loadingLabel="Saving your business profile"
        rightIcon={<ArrowRight className="size-4" />}
      >
        Save and continue
      </Button>
    </form>
  );
}

/* ================================================================== */
/* Step 3 — connect accounts                                           */
/* ================================================================== */

function ConnectRow({
  platform,
  status,
  connectedNames,
  onConnect,
  onSimulate,
  busy,
}: {
  platform: PlatformId;
  status: PlatformConnectionStatus | undefined;
  connectedNames: string[];
  onConnect: () => void;
  onSimulate: () => void;
  busy: boolean;
}) {
  const spec = PLATFORM_SPECS[platform];
  const connected = connectedNames.length > 0;
  const canSimulate = status?.canSimulate ?? !status?.configured;

  return (
    <li className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-surface p-3">
      <PlatformIcon platform={platform} size="lg" variant={connected ? 'solid' : 'soft'} />
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 text-sm font-medium text-fg">
          {spec.name}
          {spec.comingSoon && (
            <Badge tone="neutral" size="sm">
              Coming soon
            </Badge>
          )}
          {connected && (
            <Badge tone="success" size="sm" dot>
              Connected
            </Badge>
          )}
        </p>
        <p className="truncate text-xs text-muted">
          {connected ? connectedNames.join(', ') : spec.blurb}
        </p>
      </div>

      {spec.comingSoon ? (
        <Button variant="ghost" size="sm" disabled>
          Not yet
        </Button>
      ) : connected ? (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-success">
          <CircleCheck className="size-4" aria-hidden />
          Ready
        </span>
      ) : (
        <div className="flex items-center gap-2">
          {canSimulate && (
            <Button variant="ghost" size="sm" onClick={onSimulate} disabled={busy}>
              Simulate
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={onConnect}
            disabled={busy}
            rightIcon={<ExternalLink className="size-3.5" />}
          >
            Connect
          </Button>
        </div>
      )}
    </li>
  );
}

function ConnectStep({ workspaceId, onDone }: { workspaceId: string; onDone: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [pending, setPending] = useState<PlatformId | null>(null);

  const accounts = useQuery({
    queryKey: qk.accounts(workspaceId),
    queryFn: () => api.accounts.list(),
    retry: false,
  });

  const simulate = useMutation({
    mutationFn: (platform: PlatformId) => api.accounts.createSimulated({ platform }),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: qk.accounts(workspaceId) });
      toast({
        title: `${PLATFORM_SPECS[response.account.platform].shortName} simulated`,
        description: 'Publishing to it produces realistic fake results you can explore safely.',
        tone: 'success',
      });
    },
    onError: (error) => {
      toast({
        title: 'Could not create a simulated account',
        description: error instanceof ApiError ? error.message : undefined,
        tone: 'error',
      });
    },
  });

  const startConnect = async (platform: PlatformId) => {
    setPending(platform);
    try {
      const result = await api.accounts.connect(platform);
      if (result.mode === 'oauth') {
        window.location.href = result.authUrl;
        return;
      }
      if (result.mode === 'fields') {
        toast({
          title: `${PLATFORM_SPECS[platform].shortName} needs a few details`,
          description: 'Opening the Connections page — your progress here is saved.',
        });
        navigate('/connections');
        return;
      }
      if (result.canSimulate) {
        simulate.mutate(platform);
      } else {
        toast({ title: 'Not available yet', description: result.reason, tone: 'warning' });
      }
    } catch (error) {
      toast({
        title: `Could not start the ${PLATFORM_SPECS[platform].shortName} connection`,
        description: error instanceof ApiError ? error.message : undefined,
        tone: 'error',
      });
    } finally {
      setPending(null);
    }
  };

  const byPlatform = useMemo(() => {
    const map = new Map<PlatformId, string[]>();
    for (const account of accounts.data?.accounts ?? []) {
      map.set(account.platform, [...(map.get(account.platform) ?? []), account.displayName]);
    }
    return map;
  }, [accounts.data]);

  const statuses = useMemo(() => {
    const map = new Map<PlatformId, PlatformConnectionStatus>();
    for (const status of accounts.data?.platforms ?? []) map.set(status.platform, status);
    return map;
  }, [accounts.data]);

  const connectedCount = byPlatform.size;
  const unavailable = accounts.error instanceof ApiError;

  return (
    <div className="space-y-5">
      {unavailable ? (
        <Alert tone="warning" title="Connections are not reachable right now">
          You can connect accounts any time from the Connections page — skip ahead for now.
        </Alert>
      ) : (
        <Alert tone="info" title="No credentials? Simulate instead">
          Simulated accounts publish fake posts with realistic URLs so you can explore the whole
          flow before wiring up real API keys.
        </Alert>
      )}

      {accounts.isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} className="h-16 w-full" />
          ))}
        </div>
      ) : (
        <ul className="space-y-2">
          {PLATFORM_LIST.map((spec) => (
            <ConnectRow
              key={spec.id}
              platform={spec.id}
              status={statuses.get(spec.id)}
              connectedNames={byPlatform.get(spec.id) ?? []}
              busy={pending === spec.id || simulate.isPending}
              onConnect={() => void startConnect(spec.id)}
              onSimulate={() => simulate.mutate(spec.id)}
            />
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button size="lg" rightIcon={<ArrowRight className="size-4" />} onClick={onDone}>
          {connectedCount > 0 ? 'Continue' : 'I will connect later'}
        </Button>
        {connectedCount > 0 && (
          <p className="text-xs text-muted">
            {connectedCount} platform{connectedCount === 1 ? '' : 's'} ready to publish.
          </p>
        )}
      </div>
    </div>
  );
}

/* ================================================================== */
/* Step 4 — writing samples                                            */
/* ================================================================== */

function SampleRow({ sample, onDelete }: { sample: WritingSample; onDelete: () => void }) {
  return (
    <li className="flex items-start gap-3 rounded-lg border border-border bg-surface p-3">
      <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md bg-accent-soft text-accent">
        <Check className="size-3.5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-fg">{sample.title ?? 'Writing sample'}</p>
        <p className="pap-clamp-2 text-xs leading-relaxed text-muted">{sample.content}</p>
      </div>
      <Button variant="ghost" size="icon-sm" aria-label="Remove sample" onClick={onDelete}>
        <Trash2 className="size-3.5" />
      </Button>
    </li>
  );
}

function VoiceStep({ workspaceId, onDone }: { workspaceId: string; onDone: () => void }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const aiProvider = useSessionStore((state) => state.aiProvider);
  const [draft, setDraft] = useState('');

  const samples = useQuery({
    queryKey: qk.styleSamples(workspaceId),
    queryFn: () => api.style.samples(),
    retry: false,
  });

  const addSample = useMutation({
    mutationFn: () =>
      api.style.addSample({
        content: draft.trim(),
        title: draft.trim().split('\n')[0]?.slice(0, 60),
        source: 'manual',
      }),
    onSuccess: () => {
      setDraft('');
      void queryClient.invalidateQueries({ queryKey: qk.styleSamples(workspaceId) });
    },
    onError: (error) => {
      toast({
        title: 'Could not save that sample',
        description: error instanceof ApiError ? error.message : undefined,
        tone: 'error',
      });
    },
  });

  const removeSample = useMutation({
    mutationFn: (id: string) => api.style.deleteSample(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.styleSamples(workspaceId) });
    },
  });

  const analyze = useMutation({
    mutationFn: () => api.style.analyze({ scope: 'workspace' }),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: qk.styleProfile(workspaceId) });
      toast({
        title: 'Style profile created',
        description: `Learned from ${response.sampleCount} sample${response.sampleCount === 1 ? '' : 's'}.`,
        tone: 'success',
      });
      onDone();
    },
    onError: (error) => {
      toast({
        title: 'Style analysis failed',
        description: error instanceof ApiError ? error.message : undefined,
        tone: 'error',
      });
    },
  });

  const list = samples.data?.samples ?? [];
  const unavailable = samples.error instanceof ApiError;

  return (
    <div className="space-y-5">
      {aiProvider === 'mock' && (
        <Alert tone="warning" title="Demo AI is active">
          No Gemini credentials are configured, so drafts and style analysis come from a
          deterministic mock. Everything still works end to end.
        </Alert>
      )}

      <Field
        label="Paste something you have written"
        htmlFor="sample-content"
        hint="A past post, a newsletter blurb, an email to customers — anything that sounds like you. Two or three is plenty."
      >
        <Textarea
          id="sample-content"
          rows={5}
          maxRows={14}
          placeholder="Our new Ethiopian lot landed this morning and honestly it smells like blueberry jam..."
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
      </Field>

      <Button
        variant="secondary"
        leftIcon={<Plus className="size-4" />}
        disabled={draft.trim().length < 40 || unavailable}
        loading={addSample.isPending}
        onClick={() => addSample.mutate()}
      >
        Add sample
      </Button>
      {draft.trim().length > 0 && draft.trim().length < 40 && (
        <p className="text-xs text-subtle">A little more — at least 40 characters to be useful.</p>
      )}

      {samples.isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : list.length > 0 ? (
        <ul className="space-y-2">
          {list.map((sample) => (
            <SampleRow
              key={sample.id}
              sample={sample}
              onDelete={() => removeSample.mutate(sample.id)}
            />
          ))}
        </ul>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
        <Button
          size="lg"
          leftIcon={<Wand2 className="size-4" />}
          disabled={list.length === 0}
          loading={analyze.isPending}
          loadingLabel="Analyzing your writing"
          onClick={() => analyze.mutate()}
        >
          Analyze my style
        </Button>
        <Button variant="ghost" onClick={onDone}>
          {list.length ? 'Analyze later' : 'Skip for now'}
        </Button>
      </div>
    </div>
  );
}

/* ================================================================== */
/* Step 5 — done                                                       */
/* ================================================================== */

function DoneStep() {
  const navigate = useNavigate();

  const shortcuts = [
    {
      icon: PenSquare,
      title: 'Write your first post',
      body: 'Drop in a few notes and let the composer adapt them per platform.',
      to: '/composer',
    },
    {
      icon: Plug,
      title: 'Finish connecting accounts',
      body: 'Add the platforms you skipped, or swap simulated accounts for real ones.',
      to: '/connections',
    },
    {
      icon: CalendarDays,
      title: 'Plan the week',
      body: 'See everything scheduled and published on one calendar.',
      to: '/calendar',
    },
  ] as const;

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center gap-3 py-2 text-center">
        <motion.span
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 18 }}
          className="flex size-14 items-center justify-center rounded-2xl bg-accent-soft text-accent"
        >
          <PartyPopper className="size-6" aria-hidden />
        </motion.span>
        <div className="space-y-1">
          <p className="text-lg font-semibold text-fg">Your workspace is ready</p>
          <p className="text-sm text-muted">
            Everything you set here is editable later — nothing is locked in.
          </p>
        </div>
      </div>

      <ul className="grid gap-2">
        {shortcuts.map((shortcut) => (
          <li key={shortcut.to}>
            <button
              type="button"
              onClick={() => navigate(shortcut.to)}
              className="flex w-full items-start gap-3 rounded-lg border border-border bg-surface p-3 text-left transition-colors hover:border-border-strong hover:bg-elevated/50"
            >
              <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-elevated text-muted">
                <shortcut.icon className="size-4" aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-fg">{shortcut.title}</span>
                <span className="block text-xs leading-relaxed text-muted">{shortcut.body}</span>
              </span>
              <ArrowRight className="mt-2 size-4 shrink-0 text-subtle" aria-hidden />
            </button>
          </li>
        ))}
      </ul>

      <Button size="lg" fullWidth onClick={() => navigate('/', { replace: true })}>
        Go to the dashboard
      </Button>
    </div>
  );
}

/* ================================================================== */
/* Wizard                                                              */
/* ================================================================== */

const STEP_COPY: Array<{ title: string; subtitle: string; icon: typeof Sparkles }> = [
  {
    title: 'Name your workspace',
    subtitle: 'One workspace per business. Members, posts and connections all live inside it.',
    icon: Building2,
  },
  {
    title: 'Tell us about the business',
    subtitle: 'This context is fed into every AI draft, so it is worth two minutes.',
    icon: Building2,
  },
  {
    title: 'Connect where you publish',
    subtitle: 'Link the accounts you post to — or simulate them and decide later.',
    icon: Plug,
  },
  {
    title: 'Teach it your voice',
    subtitle: 'A couple of samples is all it takes for drafts to stop sounding generic.',
    icon: Sparkles,
  },
  {
    title: 'You are all set',
    subtitle: 'Here is where to go next.',
    icon: PartyPopper,
  },
];

export function OnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const workspaces = useSessionStore((state) => state.workspaces);
  const workspaceId = useWorkspaceStore((state) => state.workspaceId);

  const forceNew = searchParams.get('new') === '1';
  const hasWorkspace = workspaces.length > 0 && Boolean(workspaceId);

  // Arriving straight after first-run setup (or from the switcher's "New
  // workspace") decides whether step one is a creation form or already done.
  const [step, setStep] = useState(() => (hasWorkspace && !forceNew ? 1 : 0));
  const [completed, setCompleted] = useState<number[]>(() =>
    hasWorkspace && !forceNew ? [0] : [],
  );

  const markDone = (index: number) => {
    setCompleted((current) => (current.includes(index) ? current : [...current, index]));
    setStep(Math.min(index + 1, STEPS.length - 1));
  };

  const copy = STEP_COPY[step] ?? STEP_COPY[0]!;
  const StepIcon = copy.icon;
  const canSkip = step > 0 && step < STEPS.length - 1;

  // Every later step configures a specific workspace, and "Ready" hands you to
  // the dashboard — which bounces straight back here without one. So until a
  // workspace exists, the stepper cannot jump past step one.
  const selectStep = (index: number) => setStep(workspaceId ? index : 0);

  return (
    <div className="pap-aurora relative min-h-dvh">
      <div className="pap-dotfield pointer-events-none absolute inset-0" aria-hidden />

      <header className="relative flex items-center justify-between gap-3 px-5 py-5 md:px-10">
        <span className="flex items-center gap-2.5">
          <span
            className="flex size-8 items-center justify-center rounded-lg bg-accent text-accent-fg"
            aria-hidden
          >
            <Sparkles className="size-4" />
          </span>
          <span className="text-sm font-semibold tracking-tight text-fg">Murmur</span>
        </span>
        <div className="flex items-center gap-1">
          <ThemeToggle />
          {hasWorkspace && (
            <Button variant="ghost" size="sm" onClick={() => navigate('/', { replace: true })}>
              Skip setup
            </Button>
          )}
        </div>
      </header>

      <main className="relative mx-auto w-full max-w-2xl px-5 pb-20 md:px-8">
        <Steps
          steps={STEPS}
          current={step}
          completed={completed}
          onSelect={selectStep}
          className="pb-8"
        />

        <div className="flex items-start gap-3 pb-6">
          <span
            className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent"
            aria-hidden
          >
            <StepIcon className="size-4.5" />
          </span>
          <div className="space-y-1">
            <h1 className="text-xl font-semibold tracking-tight text-fg">{copy.title}</h1>
            <p className="text-sm leading-relaxed text-muted">{copy.subtitle}</p>
          </div>
        </div>

        <div className="pap-card p-6 shadow-pop">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={STEPS[step]?.id ?? step}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            >
              {step === 0 && <WorkspaceStep onDone={() => markDone(0)} />}
              {step === 1 &&
                (workspaceId ? (
                  <BusinessStep workspaceId={workspaceId} onDone={() => markDone(1)} />
                ) : (
                  <MissingWorkspace onBack={() => setStep(0)} />
                ))}
              {step === 2 &&
                (workspaceId ? (
                  <ConnectStep workspaceId={workspaceId} onDone={() => markDone(2)} />
                ) : (
                  <MissingWorkspace onBack={() => setStep(0)} />
                ))}
              {step === 3 &&
                (workspaceId ? (
                  <VoiceStep workspaceId={workspaceId} onDone={() => markDone(3)} />
                ) : (
                  <MissingWorkspace onBack={() => setStep(0)} />
                ))}
              {step === 4 && <DoneStep />}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex items-center justify-between gap-3 pt-5">
          {step > 0 && step < STEPS.length - 1 ? (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ArrowLeft className="size-4" />}
              onClick={() => setStep((current) => Math.max(0, current - 1))}
            >
              Back
            </Button>
          ) : (
            <span />
          )}
          {canSkip && (
            <Button variant="link" size="sm" onClick={() => setStep(step + 1)}>
              Skip this step
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}

function MissingWorkspace({ onBack }: { onBack: () => void }) {
  return (
    <div className="space-y-4">
      <Alert tone="warning" title="Pick a workspace first">
        These steps configure a specific workspace, so we need one before continuing.
      </Alert>
      <Button onClick={onBack} leftIcon={<ArrowLeft className="size-4" />}>
        Back to workspace setup
      </Button>
    </div>
  );
}

export default OnboardingPage;
